import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { fromEvent, Observable, of, Subject } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { SupportedChannelMessageEnum } from '../helpers/supported-channel-message.enum';
import { WINDOW } from './window.providers';

@Injectable({
  providedIn: 'root',
})
export class HostChannelService implements OnDestroy {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get messageReceived$(): Observable<MessageEvent> {
    return this._messageReceived$.asObservable();
  }

  get port(): MessagePort | null {
    return this._port;
  }

  readonly initialize: () => void;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly _messageReceived$: Subject<MessageEvent> = new Subject();

  private _port: MessagePort | null = null;

  constructor(
    @Inject(WINDOW) win,
    private readonly ngZone: NgZone,
  ) {
    // Why use param `win` instead of using `window`? Because it's easier to mock window behavior in unit tests
    // Why use param `win` instead of using attribute in this class? To avoid keep `window` reference in this instance.
    // Why use name `win`? To avoid mistake by using global var `window` instead of param `win`
    this.initialize = () => this._initialize(win);
  }

  ngOnDestroy(): void {
    if (this._port) {
      this._port.onmessage = null;
      this._port.close();
      this._port = null;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  postMessage<T = any>(message: T): void {
    if (this._port) {
      // @note Initially we expect to exchange data as objects but that doesn't works with Android.
      // To bypass that issue, we need to exchange data as strings.
      this._port.postMessage(JSON.stringify(message));
    } else {
      // currently that case can't be reached because user is stucked on auth page until its authentication
      console.warn('HostChannelService.postMessage(): try to post message before channel port initialization', message);
    }
  }

  // The initialize() method must be called to start listening on message which contains
  // the message channel port and messages from the message channel.
  // This method is called from HostChannelEffects when all root effects have been added by NgRx.
  _initialize(win: Window): void {
    console.log('HostChannelService.initialize()');
    if (!win) {
      return;
    }

    let channelPort$ = fromEvent<MessageEvent>(win, 'message').pipe(
      tap(messageEvent => console.log('HostChannelService: raw message received:', messageEvent)),
      filter(messageEvent => messageEvent.data === 'INIT_CHANNEL'),
      map(messageEvent => messageEvent.ports[0]),
      take(1),
    );

    // This is the second part of a workaround initialized by a script in index.html.
    // In case where message which contains channel port is sent too soon, a listener
    // in vanilla script in index.html will set the port under window.INIT_CHANNEL_PORT.
    if (win['INIT_CHANNEL_PORT']) {
      channelPort$ = of(win['INIT_CHANNEL_PORT'] as MessagePort).pipe(
        tap(port => console.log('HostChannelService: recover channel port received too soon:', port)),
      );
    }

    const onChannelMessage = (_messageEvent: MessageEvent): void => {
      console.log('HostChannelService: channel message recevied:', _messageEvent);

      let data = _messageEvent.data;
      let dataParsed = false;

      if (typeof _messageEvent?.data === 'string') {
        try {
          data = JSON.parse(data);
          dataParsed = true;
        } catch (err) {
          console.warn('Failed to parse message data:', {
            error: err,
            messageEvent: _messageEvent,
          });
        }
      }

      if (dataParsed) {
        try {
          Object.defineProperty(_messageEvent, 'data', { value: data });

          if (typeof _messageEvent.data === 'string') {
            throw new Error('Message event data cannot be override');
          }
        } catch (err) {
          console.warn('Failed to override message event data:', {
            error: err,
            messageEvent: _messageEvent,
          });

          _messageEvent = {
            type: _messageEvent.type,
            bubbles: _messageEvent.bubbles,
            cancelable: _messageEvent.cancelable,
            composed: _messageEvent.composed,
            origin: _messageEvent.origin,
            lastEventId: _messageEvent.lastEventId,
            source: _messageEvent.source,
            // ports: messageEvent.ports,
            data,
          } as MessageEvent;
        }
      }

      console.log('HostChannelService: channel message parsed', _messageEvent);

      this.ngZone.run(() => this._messageReceived$.next(_messageEvent));
    };

    channelPort$.subscribe(channelPort => {
      this._port = channelPort;
      this._port.onmessage = onChannelMessage;

      // This is the second part of a workaround initialized by a script in index.html.
      // In case where channel message are sent too soon, a listener in vanilla script
      // in index.html will add the channel message under window.INIT_CHANNEL_MESSAGES.
      for (const _messageEvent of (win['INIT_CHANNEL_MESSAGES'] || []) as MessageEvent[]) {
        console.log('HostChannelService: recover channel message received too soon:', _messageEvent);

        onChannelMessage(_messageEvent);
      }

      this.postMessage({ type: SupportedChannelMessageEnum.HEARTBEAT });
    });

    win['INIT_CHANNEL_ANGULAR'] = true;

    win.addEventListener('popstate', (event: PopStateEvent) => {
      const window = event.currentTarget as Window;
      if (window?.location.pathname === '/') {
        this.postMessage({ type: SupportedChannelMessageEnum.HISTORY_BACK });
      }
    });
  }
}
